import React from "react";
import { PhoneNumberFormatter } from "../../core/services/sharedService";
import { formatMonumentDate } from "../Common/monumentPayloads";

interface MonumentLeadDetailsProps {
    /**
     * Every normalised Monument record for this caller, all of RecordType === 'lead'.
     *
     * A list rather than a single record because one phone number legitimately carries
     * several open enquiries: the same person enquiring at more than one facility, or
     * enquiring again later, and two different people sharing a household or business line.
     * The backend dedupes on leadUuid alone, so each of those survives as its own record.
     */
    records: any[];
    /**
     * AudioRooms' label/value row renderer, passed in rather than duplicated so the panel
     * matches the tenant card exactly and there is only one copy of that markup.
     */
    makeFormatting: (header: string, data?: any) => React.ReactNode;
}

/** "Priya Raman", or "" when the lead carries neither a person name nor a business name. */
const leadName = (record: any): string =>
    `${record?.FirstName || ""} ${record?.LastName || ""}`.trim();

/**
 * Sub-heading for one card when there is more than one enquiry.
 *
 * The position prefix is not decoration. A caller can hold several enquiries at the SAME
 * facility, so name and facility are identical on every card and a heading built only from
 * the data cannot tell them apart.
 *
 * Assembled with filter(Boolean) rather than concatenated because every part is optional:
 * FirstName/LastName are `|| ''` on the backend (a business lead has no LastName at all,
 * and a lead with no business name has neither), and FacilityName is null whenever the
 * portfolio facility lookup came back empty — which nulls it on EVERY lead at once, not
 * just one. Concatenating blindly would print a dangling separator across the whole list.
 */
const cardHeading = (record: any, index: number, total: number): string =>
    [`Enquiry ${index + 1} of ${total}`, leadName(record), record?.Lead?.FacilityName]
        .filter(Boolean)
        .join(" — ");

/**
 * The caller panel for a Monument PROSPECT.
 *
 * A lead has never rented, so the tenant panel's Lease Details blocks have nothing to
 * render and its "Tenant Details" heading is simply wrong — this person is an enquiry, not
 * a tenant, and an agent needs to see that immediately to pitch the call correctly.
 *
 * Deliberately a separate component rather than another branch inside AudioRooms: that file
 * is over a thousand lines, has no test coverage, and its existing branches render live SSM
 * and Hummingbird calls. Adding a self-contained component costs AudioRooms one import and
 * one guard, and cannot affect what the other two providers display.
 */
const MonumentLeadDetails: React.FC<MonumentLeadDetailsProps> = ({ records, makeFormatting }) => {
    // Filtered here as well as guarded by the caller, so the component is safe to hand any
    // record array: a tenancy rendered through this panel would show a lead's field labels
    // over an empty Lead block rather than failing visibly.
    const leads = (Array.isArray(records) ? records : []).filter((r: any) => r?.RecordType === 'lead');
    if (!leads.length) return null;

    const multiple = leads.length > 1;
    const soleName = leadName(leads[0]);

    return (
        <>
            {/*
              * The count is load-bearing: .audio-details-card hides its scrollbar, so nothing
              * else signals that more cards sit below the fold. "Enquiries" matches the wording
              * on the cards; counted rather than named because the cards can differ in name.
              */}
            <h3 className="card-title">
                {multiple ? `Lead Details - ${leads.length} Enquiries` : `Lead Details${soleName ? ` - ${soleName}` : ""}`}
            </h3>
            {/*
              * Rendered in the order the backend sent them, deliberately NOT re-sorted here.
              *
              * That order is load-bearing beyond this panel: the call-summary pop-up derives
              * its default selection from the same array, and the call-end auto-submit writes
              * the note to that default. Sorting only the panel (by enquiry date, say) would
              * put one lead at the top of the screen while the note silently went to another.
              * Any re-ordering has to happen in the backend, for both consumers at once.
              */}
            {leads.map((record: any, index: number) => {
                const lead = record?.Lead || {};
                const address = record?.Address
                    ? `${record.Address}, ${record?.City || ""}, ${record?.State?.Name || ""}`
                    : " - ";
                return (
                    <div className="m-2 tenantBorder" key={record?.LeadUuid || index}>
                        {multiple && <h4>{cardHeading(record, index, leads.length)}</h4>}
                        {makeFormatting(
                            "Phone Number",
                            record?.Phone
                                ? PhoneNumberFormatter(record.Phone.toString(), record.Phone.toString().length)
                                : " - Not Available "
                        )}
                        <div className="row">
                            <div className="col-4">Full Address</div>
                            <div className="col-8">: {address}</div>
                        </div>
                        {makeFormatting("Enquiry Status", lead.LeadStatus)}
                        {makeFormatting("Enquired On", formatMonumentDate(lead.EnquiredDate))}
                        {makeFormatting("Lead Source", lead.LeadSource)}
                        {makeFormatting("Facility", lead.FacilityName)}
                        {makeFormatting("Location", lead.LocationAddress)}
                        {/* Only shown when the prospect named a move-in date; most leads have none. */}
                        {lead.DesiredMoveIn ? makeFormatting("Desired Move-In", formatMonumentDate(lead.DesiredMoveIn)) : null}
                    </div>
                );
            })}
        </>
    );
};

export default MonumentLeadDetails;
